/* eslint-disable import/no-extraneous-dependencies */
import React, { useCallback, useState } from "react";
import { Box } from "@mui/material";
import { Virtuoso } from "react-virtuoso";
import CategorySidebarComponent from "./category-sidebar";
import ValuesListHeaderComponent from "./values-list-header";
import ValueItemComponent from "./value-item";
import { CategoryFooterFactory } from "./category-footer-factory";
import CleanseDialog from "./cleanse-dialog";
import { DistinctValue } from "../../types";
import { CategoryConfig } from "../../config/categories-config";
import useEntityFields from "../../hooks/use-entity-fields";
import { useNotification } from "../../../../context/notification-provider";
import { cleanseField } from "../../services/cleaning-data-service";
import useTemplates from "../../../templates/hooks/use-templates";

interface ValuesContentProps {
  categories: (CategoryConfig & { count: number })[];
  selectedCategory: number;
  dragOverCategory: number | null;
  selectedCategoryLabel: string;
  allChecked: boolean;
  indeterminate: boolean;
  mainValues: DistinctValue[];
  checked: { [key: string]: boolean };
  selectedItem: DistinctValue | null;
  anchorEl: { [key: string]: HTMLElement | null };
  fieldId: number | undefined;
  isLoading: boolean;
  onCategorySelect: (categoryId: number) => void;
  onSelectAll: (isChecked: boolean) => void;
  onCheckValue: (valueId: string, isChecked: boolean) => void;
  onItemClick: (item: DistinctValue) => void;
  onOpenMenu: (valueId: string, event: React.MouseEvent<HTMLElement>) => void;
  onCloseMenu: (valueId: string) => void;
  onMoveValue: (valueId: string, targetCategory: number) => void;
  onDragStart: (e: React.DragEvent<HTMLDivElement>, valueId: string) => void;
  onDragOver: (e: React.DragEvent<HTMLDivElement>, categoryId: number) => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent<HTMLDivElement>, categoryId: number) => void;
  getCategoryBackground: (categoryId: number) => string | undefined;
  refreshDistinctValues: () => void;
  onOpenChart: (item: DistinctValue) => void;
}

/**
 * ValuesContent displays the category sidebar and values list
 * for the selected tile. Includes drag & drop functionality.
 */
const ValuesContent: React.FC<ValuesContentProps> = ({
  categories,
  selectedCategory,
  dragOverCategory,
  selectedCategoryLabel,
  allChecked,
  indeterminate,
  mainValues,
  checked,
  selectedItem,
  anchorEl,
  fieldId,
  isLoading,
  onCategorySelect,
  onSelectAll,
  onCheckValue,
  onItemClick,
  onOpenMenu,
  onCloseMenu,
  onMoveValue,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  getCategoryBackground,
  refreshDistinctValues,
  onOpenChart,
}) => {
  // Fetch entity fields once at this level to avoid refetching on category changes
  const { fields: availableFields, isLoading: isLoadingFields } =
    useEntityFields();

  // Fetch templates for cleanse functionality
  const { templates, isLoading: isLoadingTemplates } = useTemplates();
  const { showNotification } = useNotification();

  // State for cleanse dialog
  const [cleanseDialogOpen, setCleanseDialogOpen] = useState(false);
  const [isCleansing, setIsCleansing] = useState(false);

  const handleOpenCleanseDialog = useCallback(() => {
    setCleanseDialogOpen(true);
  }, []);

  const handleCloseCleanseDialog = useCallback(() => {
    setCleanseDialogOpen(false);
  }, []);

  const handleCleanse = useCallback(
    async (templateId: number) => {
      if (!fieldId) {
        showNotification({
          message: "No field selected.",
          severity: "error",
          duration: 3000,
        });
        return;
      }

      setIsCleansing(true);

      try {
        await cleanseField(fieldId, templateId);
        showNotification({
          message: "Field cleansed successfully.",
          severity: "success",
          duration: 3000,
        });
        setCleanseDialogOpen(false);
        refreshDistinctValues();
      } catch (err) {
        showNotification({
          message: `Failed to cleanse field. Reason: ${(err as Error).message}`,
          severity: "error",
          duration: 3000,
        });
      } finally {
        setIsCleansing(false);
      }
    },
    [fieldId, refreshDistinctValues, showNotification],
  );

  const renderItem = useCallback(
    (index: number, val: DistinctValue) => (
      <ValueItemComponent
        key={val.id}
        valueText={val.value}
        checked={!!checked[val.id]}
        isSelected={selectedItem?.id === val.id}
        anchorEl={anchorEl[val.id] || null}
        categories={categories}
        selectedCategory={selectedCategory}
        onCheck={(isChecked) => onCheckValue(val.id, isChecked)}
        onItemClick={() => onItemClick(val)}
        onDragStart={(e) => onDragStart(e, val.id)}
        onOpenMenu={(e) => onOpenMenu(val.id, e)}
        onCloseMenu={() => onCloseMenu(val.id)}
        onMoveValue={(targetCategory) => onMoveValue(val.id, targetCategory)}
        onOpenChart={() => onOpenChart(val)}
      />
    ),
    [
      checked,
      selectedItem,
      anchorEl,
      categories,
      selectedCategory,
      onCheckValue,
      onItemClick,
      onDragStart,
      onOpenMenu,
      onCloseMenu,
      onMoveValue,
      onOpenChart,
    ],
  );

  return (
    <Box sx={{ display: "flex", height: 610 }}>
      <CategorySidebarComponent
        categories={categories}
        selectedCategory={selectedCategory}
        dragOverCategory={dragOverCategory}
        isLoading={isLoading}
        onCategorySelect={onCategorySelect}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        getCategoryBackground={getCategoryBackground}
        onCleanse={handleOpenCleanseDialog}
      />
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 482,
        }}
      >
        <ValuesListHeaderComponent
          title={selectedCategoryLabel}
          allChecked={allChecked}
          indeterminate={indeterminate}
          onSelectAll={onSelectAll}
        />
        <Box sx={{ flex: 1 }}>
          <Virtuoso data={mainValues} itemContent={renderItem} />
        </Box>
        <CategoryFooterFactory
          selectedCategory={selectedCategory}
          fieldId={fieldId}
          checked={checked}
          mainValues={mainValues}
          selectedItem={selectedItem}
          refreshDistinctValues={refreshDistinctValues}
          availableFields={availableFields}
          isLoadingFields={isLoadingFields}
        />
      </Box>
      <CleanseDialog
        open={cleanseDialogOpen}
        templates={templates}
        onClose={handleCloseCleanseDialog}
        onConfirm={handleCleanse}
        isLoading={isCleansing || isLoadingTemplates}
      />
    </Box>
  );
};

export default ValuesContent;
